import React, { ComponentProps, FunctionComponent } from "react";

import styled from "styled-components";
import { rgba } from "polished";
import { AnimatePresence, motion } from "framer-motion";

import { TracPageLink } from "@services/tracpage/types/trac-page-link";

import AUDIOWAVE_DEMO from "./audiowave-demo.json";
import useIsMobile from "../../hooks/is-mobile";
import { StyledAudiowave } from "./audiowave";
import ArrowRight from "../../icons/arrow-right";
import CloseIcon from "../../icons/close";
import { CoverImage } from "../cover-image";
import { Play } from "../link-components/play";
import CoverPlayIcon from "../../icons/cover-play";
import { WrapperInfo } from "../link-components/wrapper-info";
import { Info } from "../link-components/text-info";
import { VisisbilityButtons } from "../edit-controls/visibility-buttons";
import { useEditMode } from "../../contexts/edit";
import { TracPageFeatureItemType } from "@services/tracpage/types/trac-page-feature-item";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { getSocialIcon } from "../../utils/get-social-icon";
import { ItemsOrderButtons } from "../edit-controls/items-order-buttons";

interface SongLinkProps {
  data: TracPageLink;
  dsps: TracPageLink[];
  className?: string;
  artistName?: string | null;
  hasDelay?: number;
  inSidebar?: boolean;
  noAnimation?: boolean;
  handleClick?: () => void;
  isHidden?: boolean;
  orderButtonsDisabledState?: ComponentProps<
    typeof ItemsOrderButtons
  >["disabledState"];
  isMainPage?: boolean;
}

export const SongLink: FunctionComponent<SongLinkProps> = ({
  data,
  dsps,
  className,
  artistName,
  inSidebar,
  handleClick,
  isHidden = false,
  orderButtonsDisabledState,
  isMainPage = false,
}) => {
  const { editMode } = useEditMode();
  const isMobile = useIsMobile();
  const [opened, setOpened] = React.useState(false);

  const handleMenuOpen = () => {
    setOpened((prevState) => !prevState);
  };

  return data.title ? (
    <SongLinkItem
      isHidden={isHidden}
      className={`${className || ""} ${data.highlight ? "highlight" : ""}`}
      inSidebar={inSidebar}
      editMode={!inSidebar && editMode}
    >
      <div className="song-link-info">
        <div className="cover-image">
          <CoverImage
            src={data.avatar}
            alt={data.title}
            variant="squareSmall"
          />
          <Play className={`play-icon ${data.highlight ? "highlight" : ""}`}>
            <CoverPlayIcon />
          </Play>
        </div>
        <WrapperInfo>
          <Info
            className={`${data.highlight ? "highlight" : ""}`}
            inSidebar={inSidebar}
          >
            <h3>{data.title}</h3>
            <p>{artistName}</p>
          </Info>
          {isMobile && !inSidebar ? (
            <div className="dsps-row">
              {dsps.slice(0, 1).map((e, i) => (
                <a
                  className="icon-wrapper"
                  key={`${i}-${e.platform}`}
                  aria-label={e.platform}
                  href={e.url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {getSocialIcon(e.platform ?? "custom")}
                </a>
              ))}
              <a className="icon-wrapper" onClick={handleClick} href={data.url}>
                <ArrowRight className="arrow-icon" />
              </a>
            </div>
          ) : inSidebar ? null : (
            <div className="dsps-row">
              {dsps.slice(0, 3).map((e, i) => (
                <a
                  className="icon-wrapper"
                  key={`${i}-${e.platform}`}
                  aria-label={e.platform}
                  href={e.url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {getSocialIcon(e.platform ?? "custom")}
                </a>
              ))}
              {dsps.length > 3 ? (
                <div
                  className="icon-wrapper-menu"
                  tabIndex={0}
                  onClick={handleMenuOpen}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleMenuOpen();
                    }
                  }}
                >
                  <a className={`icon-wrapper ${opened ? "active" : ""}`}>
                    {opened ? (
                      <CloseIcon className="icon" />
                    ) : (
                      <span>+{dsps.length - 3}</span>
                    )}
                  </a>
                  <AnimatePresence>
                    {opened ? (
                      <motion.div
                        className="menu"
                        initial={{ opacity: 0, y: -10 }}
                        animate={
                          opened ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }
                        }
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {dsps.slice(3).map((e, i) => (
                          <a
                            className="icon-wrapper-menu-item"
                            key={`${i}-${e.platform}`}
                            aria-label={e.platform}
                            href={e.url}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {e.platform}
                          </a>
                        ))}
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              ) : null}
              {!editMode && (
                <a
                  className="icon-wrapper"
                  onClick={handleClick}
                  href={data.url}
                >
                  <ArrowRight className="arrow-icon" />
                </a>
              )}
            </div>
          )}
        </WrapperInfo>
      </div>
      <StyledAudiowave className="audiowave" rectsItems={AUDIOWAVE_DEMO} />
      {!inSidebar && (
        <div className="edit-controls">
          <VisisbilityButtons
            disabledState={{ feature: isHidden }}
            itemId={data.id ?? ""}
            sectionId={SectionIdentifier.MUSIC}
            itemType={TracPageFeatureItemType.TracLink}
          />
          {!isHidden && (
            <ItemsOrderButtons
              itemId={data.id ?? ""}
              sectionId={SectionIdentifier.MUSIC}
              disabledState={orderButtonsDisabledState}
              forMainPageOrder={isMainPage}
            />
          )}
        </div>
      )}
    </SongLinkItem>
  ) : null;
};

const SongLinkItem = styled(motion.div)<{
  inSidebar?: boolean;
  editMode: boolean;
  isHidden: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  border: ${({ editMode, theme }) =>
    editMode ? `1px solid ${rgba(theme.colors.primary, 0.1)}` : "inherit"};
  padding: ${({ editMode }) => (editMode ? "24px" : "0")};

  --icon-size: 32px;

  &.highlight {
    padding: 16px;
    border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.2)};
    background: ${({ theme }) => theme.colors.highlightBackground};
    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
      border: 1px solid ${({ theme }) => rgba(theme.colors.highlightLight, 0.5)};
      background: ${({ theme }) => rgba(theme.colors.highlightLight, 0.02)};
    }
  }

  .edit-controls {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  }

  .audiowave {
    opacity: ${({ isHidden }) => (isHidden ? 0.5 : 1)};
  }

  .song-link-info {
    opacity: ${({ isHidden }) => (isHidden ? 0.5 : 1)};

    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
    width: 100%;

    .cover-image {
      position: relative;
      width: ${({ theme }) => theme.sizes.coverSizeSmall};
      height: ${({ theme }) => theme.sizes.coverSizeSmall};
      &:hover {
        .play-icon {
          opacity: 1;
        }
      }
    }
  }

  .dsps-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
  }

  .icon-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    width: var(--icon-size);
    height: var(--icon-size);
    aspect-ratio: 1;
    border-radius: 50%;
    background-color: ${({ theme }) => rgba(theme.colors.primary, 0.1)};
    cursor: pointer;
    padding: 0;
    overflow: hidden;
    border: 1px solid transparent;
    transition: background-color
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`},
      border
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};

    > svg {
      fill: ${({ theme }) => theme.colors.primary};
      max-height: 12px;
      max-width: 16px;
      width: 100%;
      path {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }

    .arrow-icon {
      fill: ${({ theme }) => theme.colors.primary};
      path {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }

    span {
      color: ${({ theme }) => theme.colors.primary};
      font-size: 12px;
      font-weight: 700;
    }
    &:focus,
    &:focus-within,
    &:focus-visible,
    &:hover,
    &.active {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.primary};
    }

    &:focus,
    &:focus-within,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 4px 2px ${({ theme }) => rgba(theme.colors.primary, 0.25)};
    }

    .icon {
      width: 8px;
      height: 8px;
    }
  }

  .icon-wrapper-menu {
    position: relative;

    &:focus-visible {
      outline: none;
      .icon-wrapper {
        background-color: ${({ theme }) => theme.colors.background};
        border-color: ${({ theme }) => theme.colors.primary};
      }
    }

    .menu {
      min-width: 144px;
      position: absolute;
      top: calc(var(--icon-size) + 20px);
      right: 0;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 6px;
      padding: 16px 0;
      background-color: ${({ theme }) => theme.colors.itemBackground};
      border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.2)};
      border-radius: 4px;
      z-index: 1;

      .icon-wrapper-menu-item {
        font-size: 14px;
        font-weight: 700;
        line-height: 24px;
        color: ${({ theme }) => theme.colors.primary};
        width: 100%;
        flex-shrink: 0;
        padding: 2px 16px;
        background-color: transparent;
        transition: background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        &:focus,
        &:focus-within,
        &:focus-visible,
        &:hover {
          background-color: ${({ theme }) => rgba(theme.colors.primary, 0.2)};
        }

        &:focus,
        &:focus-within,
        &:focus-visible {
          outline: none;
        }
      }
    }
  }
`;
